import React, { useState, useRef, useEffect } from 'react';
import CsvDownloader from 'react-csv-downloader';
import { useQuery, useLazyQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import logError from 'app/lib/log-error';
import { Pagination } from '@td/shared_utils';
import SEARCH_PROVIDERS from '../queries/provider-basic-search.graphql';
import SEARCH_ALL_PROVIDERS from '../queries/provider-basic-search-fetch-all.graphql';
import classnames from 'classnames';
import * as _ from 'lodash';
import moment from 'moment';
import styles from '../styles.module.scss';

const ProviderBasicSearchResults = ({ searchParams, setIsFetching, showOnShift }) => {
  const [csvData, setCSVData] = useState([]);
  const [currentDate, setCurrentDate] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const date = new Date();
  const csvButtonRef = useRef(null);
  const MAX_PER_PAGE = 15;

  const providerOnShift = _.pick(searchParams, 'onShift', 'onShiftStartDt', 'onShiftEndDt')
  const queryVariables = {
    params: searchParams,
    providerOnShift: providerOnShift,
    first:  MAX_PER_PAGE
  };

  useEffect(() => {
    if (csvData.length === 0) return;
    csvButtonRef.current.click();
    setIsSubmitting(false);
  }, [csvData]);

  const searchProvidersQueryName = 'providerBasicSearch';

  const { loading, error, data, fetchMore } = useQuery(SEARCH_PROVIDERS, {
    variables: queryVariables
  });

  const [loadAllData] = useLazyQuery(SEARCH_ALL_PROVIDERS, {
    // TODO: better solution, hopefully one day apollo fixes completed to be called
    // on cache fetches
    fetchPolicy: 'network-only',
    onError:     e => {
      logError({ e }, 'Failed to load CVS data for provider basic search');
      alert('Something went wrong while exporting the data. Please reload the page and try again.');
    },
    onCompleted: d => {
      setCSVData(d.providerBasicSearch.edges.map(({ node: {
        name,
        isActive,
        id,
        affiliationGroupName,
        clockedIn,
        roles,
        stateLicenses
      } }) => {
        return {
          Name:                   name,
          Status:                 (isActive ? 'Active' : 'Inactive'),
          ID:                     id,
          // eslint-disable-next-line no-unneeded-ternary
          'Affiliation Group':    affiliationGroupName ? affiliationGroupName : '',
          'Clocked In/Out State': (clockedIn ? 'Clocked In' : 'Clocked Out'),
          'Minimum Age':          (roles.length ? roles.reduce((acc, curr) => Math.min(acc, curr.minAge), Infinity) : ''),
          'Maximum Age':          (roles.length ? roles.reduce((acc, curr) => Math.max(acc, curr.maxAge), -Infinity) : ''),
          'State Licenses':       (stateLicenses.length ? stateLicenses.join(' ') : '')
        };
      }));
    }
  });

  if (error) {
    logError({ error }, 'Failed to fetch provider search results.');
    setIsFetching(false);
    return <div className={styles.updateMessage}>Search Results unavailable, please refresh the page and try again.</div>;
  }

  if (loading || !data) {
    return <div className={styles.updateMessage}>Loading results...</div>;
  }

  const {
  providerBasicSearch: { edges, pageInfo: {
    hasNextPage,
    hasPreviousPage,
    startCursor,
    endCursor
  } },
  currentUserAdmin: {
    hasProviderImpersonationAccess
  } } = data;

  if (edges.length === 0) {
    setIsFetching(false);
    return (
      <div>
        <h3>Providers</h3>
        <div className="no-results">
          Sorry, no providers matched your search.
        </div>
      </div>
    );
  }

  const handleCSVSubmit = e => {
    e.preventDefault();
    setIsSubmitting(true);
    loadAllData({ variables: { params: { ...searchParams, fetchAll: true } } });
    setCurrentDate(
      `${date.getMonth()}-${date.getDate()}-${date.getFullYear()}-${date.getHours() < 10 ? 0 + date.getHours() : date.getHours()}${date.getMinutes()}`
    );
  };

  const handleImpersonationSubmit = (e, id) => {
    e.preventDefault();
    const csrfToken = document.querySelector("meta[name='csrf-token']").getAttribute('content');

    fetch(Routes.impersonate_provider_path(id), {
      method:  'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
        ...window.TELADOC_ENVELOP
      }
    }).then(resp => resp.json())
      .then(impersonateData => {
        // data is returned in snake case :(
        const { impersonation_url } = impersonateData;
        // eslint-disable-next-line camelcase
        if (impersonation_url) window.open(impersonation_url, '_blank');
      });
  }

  const onShiftPeriodNow = (periods) => {
    return _.find(periods, (period)=> {
      const { startDateTime, endDateTime } = period;
      return moment().isSameOrBefore(endDateTime) && moment().isSameOrAfter(startDateTime)
    })
  }

  const onShiftPeriodLine = (period, key) => {
    return (
      <p className={styles['on-shift-periods']} key={key}>
        {onShiftDateTime(period.startDateTime)}
        {' - '}
        {onShiftDateTime(period.endDateTime)}
      </p>
    )
  }

  const onShiftDateTime = (dateTime) => {
    return (
      <React.Fragment>
        <span>{moment(dateTime).format('MM/DD/YY ')}</span>
        <span className={styles['time']}>{moment(dateTime).format('h:mm A')}</span>
      </React.Fragment>
    )
  };

  const setProviders = results => {
    setIsFetching(false);
    return results.map(({ node: {
      name,
      username,
      isActive,
      id,
      clockedIn,
      affiliationGroupName,
      onShiftPeriods,
      roles
    } }) =>
      <tr
        className={
          classnames('provider', {
            [styles['clocked-out-and-on-shift']]: showOnShift && !clockedIn && onShiftPeriodNow(onShiftPeriods)
          })
        }
        key={id}
      >
        <td >{name}</td>
        <td>{username}</td>
        <td>{isActive ? 'Active' : 'Inactive'}</td>
        <td>{id}</td>
        <td>{affiliationGroupName}</td>
        {showOnShift &&
          <td>{onShiftPeriods.map((period, ind) => onShiftPeriodLine(period, ind))}</td>
        }
        <td>{clockedIn ? 'Clocked In' : 'Clocked Out'}</td>
        <td>{roles.length ? roles.reduce((acc, curr) => Math.min(acc, curr.minAge), Infinity) : null}</td>
        <td>{roles.length ? roles.reduce((acc, curr) => Math.max(acc, curr.maxAge), -Infinity) : null}</td>
        <td className="actions">
          <div>
            <a href={Routes.provider_path(id)}>View</a>
          </div>
          {hasProviderImpersonationAccess ? (
            <div>
              <a
                className="impersonation-button"
                onClick={e => handleImpersonationSubmit(e, id)}
               >
                Impersonate
              </a>
            </div>
          ) : null}
        </td>
      </tr>);
  };

  return (
    <div>
      <h3>Providers</h3>
      <table className="person_list providers">
        <thead>
          <tr>
            <th>Name</th>
            <th>Username</th>
            <th>Status</th>
            <th>ID</th>
            <th>Affiliation Group</th>
            {showOnShift &&
              <th>Calendar</th>
            }
            <th>Clocked In/Out Status</th>
            <th>Min Age</th>
            <th>Max Age</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {setProviders(edges)}
        </tbody>
        <tfoot>
          <tr>
            <input
              type="submit"
              value={isSubmitting ? 'exporting ...' : 'export'}
              disabled={isSubmitting}
              onClick={handleCSVSubmit}
              id={styles.csvSubmit}
            />
            <i id={styles.searchLimitPrompt}>Export limited to first 1000 records.</i>
            <CsvDownloader
              filename={`Provider_Search_Results_${currentDate}`}
              datas={csvData}
            >
              <input type="hidden" ref={csvButtonRef} />
            </CsvDownloader>
          </tr>
        </tfoot>
      </table>
      <Pagination
        fetchMore={fetchMore}
        hasPreviousPage={hasPreviousPage}
        hasNextPage={hasNextPage}
        startCursor={startCursor}
        endCursor={endCursor}
        maxPageSize={MAX_PER_PAGE}
        styling={styles}
        queryName={searchProvidersQueryName}
      />
    </div>
  );
};

ProviderBasicSearchResults.propTypes = {
  searchParams:  PropTypes.object,
  setIsFetching: PropTypes.func
};

export default ProviderBasicSearchResults;
